import React from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Stack,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SchoolIcon from '@mui/icons-material/School';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import FactoryIcon from '@mui/icons-material/Factory';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Styled components

  const HeroSection = styled(Box)(({ theme }) => ({
    position: 'relative',
    height: '50vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    backgroundImage: `url(${process.env.PUBLIC_URL}/Bio-energy-img.jpg)`,
    padding: theme.spacing(4),
    marginBottom: theme.spacing(8),
    // Create the black overlay
  "::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)", // Adjust opacity here (0.5 = 50% black)
    zIndex: 1,
  },

  // Ensure content appears above the overlay
  "& > *": {
    position: "relative",
    zIndex: 2,
  },
  }));
  
const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  color: 'white',
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 60,
  height: 60,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  background: theme.palette.background.paper,
}));

const BioEnergy = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box>
      {/* Hero Section */}
      <HeroSection>
        <Box sx={{ textAlign: 'center', maxWidth: 800 }}>
          <Typography variant="h2" component="h1" gutterBottom>
            Bio Energy
          </Typography>
          <Typography variant="h5">
            Powering a Sustainable Future for India
          </Typography>
        </Box>
      </HeroSection>

      <Container maxWidth="lg" sx={{marginTop:"20px"}}>
        {/* Overview Section */}
        <Card sx={{ mb: 6, mt: 8, position: 'relative', zIndex: 1 }}>
          <CardContent>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h4" gutterBottom color="primary">
                  Energy Independence Initiative
                </Typography>
                <Typography variant="body1" paragraph>
                  As the nation takes a fast track towards growth and success, energy access and security remains one of our key challenges. With conventional energy sources becoming scarcer, we're focusing on renewable bio energy solutions that can lead to energy independence and sustainable development.
                </Typography>
                <Stack spacing={2}>
                  <Box display="flex" alignItems="center">
                    <CheckCircleIcon color="primary" sx={{ mr: 2 }} />
                    <Typography>Partner with petroleum ministry</Typography>
                  </Box>
                  <Box display="flex" alignItems="center">
                    <CheckCircleIcon color="primary" sx={{ mr: 2 }} />
                    <Typography>Nationwide presence and network</Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  component="img"
                  src="/Bio-energy-img.jpg"
                  alt="Bio Energy Initiative"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: 2,
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Bio Energy Urja Utsav Section */}
        <Card sx={{ mb: 6, bgcolor: theme.palette.primary.main, color: 'white' }}>
          <CardContent>
            <Grid container spacing={4}>
              <Grid item xs={12} md={12}>
                <Typography variant="h4" gutterBottom>
                  Bio Energy Urja Utsav
                </Typography>
                <Typography paragraph>
                  A two-day celebration of sustainable energy, bringing together students, farmers, technocrats, and citizens to explore the future of bio-energy in India.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <StatsCard>
                      <Typography variant="h4">4,200+</Typography>
                      <Typography variant="subtitle1">Students Participated</Typography>
                    </StatsCard>
                  </Grid>
                  <Grid item xs={6}>
                    <StatsCard>
                      <Typography variant="h4">550K</Typography>
                      <Typography variant="subtitle1">Live Stream Viewers</Typography>
                    </StatsCard>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h5" gutterBottom color="primary">
                      Key Highlights
                    </Typography>
                    <Stack spacing={2}>
                      
                     
                  <Box display="flex" alignItems="center">
                    <CheckCircleIcon color="primary" sx={{ mr: 2 }} />
                    <Typography>Technical sessions and workshops</Typography>
                    
                  </Box>
                  <Box display="flex" alignItems="center">
                    <CheckCircleIcon color="primary" sx={{ mr: 2 }} />
                    <Typography> Bio diesel rally and competitions</Typography>
                    
                  </Box>
                  <Box display="flex" alignItems="center">
                    <CheckCircleIcon color="primary" sx={{ mr: 2 }} />
                    <Typography> Exhibition and technical tours</Typography>
                    
                  </Box>
               
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* World Bio Fuel Day Section */}
        <Card sx={{ mb: 6 }}>
          <CardContent>
            <Typography variant="h4" align="center" gutterBottom color="primary">
              World Bio Fuel Day Awareness Programs
            </Typography>
            <Typography align="center" paragraph sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}>
              Taking the initiative forward with various activities across 100 districts to celebrate World Bio Fuel Day and spread awareness about sustainable energy solutions.
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <IconWrapper sx={{margin:"auto"}}>
                      <SchoolIcon color="primary" sx={{ fontSize: 30 }} />
                    </IconWrapper>
                    <Typography variant="h6" gutterBottom>
                      Education
                    </Typography>
                    <Typography>
                      Engaging students and educators in bio-fuel awareness
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <IconWrapper sx={{margin:"auto"}}>
                      <AgricultureIcon color="primary" sx={{ fontSize: 30 }} />
                    </IconWrapper>
                    <Typography variant="h6" gutterBottom>
                      Agriculture
                    </Typography>
                    <Typography>
                      Supporting farmers in sustainable bio-fuel production
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <IconWrapper sx={{margin:"auto"}}>
                      <FactoryIcon color="primary" sx={{ fontSize: 30 }} />
                    </IconWrapper>
                    <Typography variant="h6" gutterBottom>
                      Industry
                    </Typography>
                    <Typography>
                      Collaborating with businesses for bio-energy innovation
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default BioEnergy;